<script setup lang="ts">
import { computed, onMounted } from "vue";

import LoadingSpinner from "~/components/dumb/LoadingSpinner.vue";
import GlobalPopper, { PopperType } from "~/components/globalPopper";
import { convertToMarkdown } from "~/components/text/transformers";
import { ENTITY_CARD_PREVIEW_SHOW_DELAY_MS } from "~/constants/app";
import { Placement } from "~/shared/enums";
import type { Chat } from "~/shared/types";
import { useDataStore, usePageStore } from "~/stores";
import { getItemCountText } from "~/utils/common";
import { CHAT_NO_TITLE_LABEL } from "~/utils/entityTitle";

const props = defineProps<{
  chat: Chat;
}>();

const dataStore = useDataStore();
const pageStore = usePageStore();

const liveChat = computed(() => dataStore.getChatByDuidIfLoaded(props.chat.duid) ?? props.chat);
const messages = computed(() => dataStore.getTextMessages(props.chat.duid));
const messageCountText = computed(() =>
  messages.value.length === 0
    ? "No messages"
    : getItemCountText(messages.value.length, "message", { noSpecial: true, capitalize: true })
);
const lastMessageText = computed(() => {
  const lastMessage = messages.value[messages.value.length - 1];
  if (!lastMessage) {
    return "";
  }
  const text = convertToMarkdown(lastMessage.content);
  return messages.value.length > 1 ? `…${text}` : text;
});
const isWorking = computed(() => liveChat.value.working || dataStore.isChatAgentWorking(liveChat.value));

onMounted(() => {
  if (!dataStore.areChatMessagesLoaded(props.chat.duid)) {
    dataStore.loadChatMessagesForChat(props.chat.duid);
  }
});
</script>

<template>
  <GlobalPopper
    :type="PopperType.TOOLTIP"
    no-auto-focus
    :disabled="pageStore.isMobile"
    :placement="Placement.RIGHT"
    :delay="{ show: ENTITY_CARD_PREVIEW_SHOW_DELAY_MS, hide: 0 }"
    :theme="`tooltip-arrowless-${pageStore.theme}`"
    class="flex min-w-0">
    <slot />

    <template #popper>
      <div class="flex w-64 flex-col gap-1.5 rounded-sm border border-md bg-std p-2 text-sm text-lt">
        <div class="flex items-start gap-2">
          <span class="break-words min-w-0 grow font-semibold hyphens-auto">
            {{ liveChat.title || CHAT_NO_TITLE_LABEL }}
          </span>
          <LoadingSpinner v-if="isWorking" tiny immediate class="w-max shrink-0" />
          <div v-if="!liveChat.read" class="size-2 shrink-0 rounded-full bg-primary-base" aria-label="Unread chat" />
        </div>
        <span class="text-vlt">{{ messageCountText }}</span>
        <span v-if="lastMessageText" class="line-clamp-3 break-words hyphens-auto">{{ lastMessageText }}</span>
      </div>
    </template>
  </GlobalPopper>
</template>
